// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { createCachedValue, isFunction, isString, objDefine, symbolFor } from "@nevware21/ts-utils";
import { _DYN_LENGTH } from "../../../__DynamicConstants";
import { STR_EMPTY } from "../../../constants/InternalConstants";
import { createW3cTraceState, isW3cTraceState } from "../../../telemetry/W3cTraceState";
var _otelTraceState;
function _initOTelTraceStateSymbol() {
    if (!_otelTraceState) {
        _otelTraceState = createCachedValue(symbolFor("otTraceState"));
    }
    return _otelTraceState;
}
/*#__NO_SIDE_EFFECTS__*/
function _createOTelTraceState(traceState) {
    if (!_otelTraceState) {
        _otelTraceState = _initOTelTraceStateSymbol();
    }
    var otTraceState = {
        set: function (key, value) {
            var newState = createW3cTraceState(STR_EMPTY, traceState);
            newState.set(key, value);
            return _createOTelTraceState(newState);
        },
        unset: function (key) {
            var newState = createW3cTraceState(STR_EMPTY, traceState);
            newState.del(key);
            return _createOTelTraceState(newState);
        },
        get: function (key) {
            return traceState.get(key);
        },
        serialize: function () {
            var headers = traceState.hdrs(1);
            if (headers[_DYN_LENGTH /* @min:%2elength */] > 0) {
                return headers[0];
            }
            return STR_EMPTY;
        }
    };
    objDefine(otTraceState, _otelTraceState.v, { g: function () { return traceState; } });
    return otTraceState;
}
/**
 * Identifies if the provided value is an OpenTelemetry TraceState object
 * @param value - The value to check
 * @returns true if the value is an OpenTelemetry TraceState object otherwise false
 * @remarks The OpenTelemetry TraceState is an immutable object, meaning that any changes made to the trace state will
 * @since 3.4.0
 */
/*#__NO_SIDE_EFFECTS__*/
export function isOTelTraceState(value) {
    if (!_otelTraceState) {
        _otelTraceState = _initOTelTraceStateSymbol();
    }
    if (value && value[_otelTraceState.v]) {
        return true;
    }
    return value && isFunction(value.serialize) && isFunction(value.unset) && isFunction(value.get) && isFunction(value.set);
}
/**
 * Returns an OpenTelemetry compatible instance of the trace state, an important distinction
 * between an {@link IW3cTraceState} and an {@link IOTelTraceState} is that the OpenTelemetry version
 * is immutable, meaning that any changes made to the trace state will create and return a new
 * instance for the {@link IOTelTraceState.set} and {@link IOTelTraceState.unset} methods.
 * @param value - The current trace state value
 * @returns An OpenTelemetry compatible instance of the trace state
 * @remarks The OpenTelemetry TraceState is an immutable object, meaning that any changes made to the trace state will
 * @since 3.4.0
 */
/*#__NO_SIDE_EFFECTS__*/
export function createOTelTraceState(value) {
    var traceState = null;
    if (isOTelTraceState(value)) {
        var parentTraceState = void 0;
        if (_otelTraceState) {
            // Only attempt the lookup if the symbol has been created and therefore possibly
            // assigned to a parent trace state
            parentTraceState = value[_otelTraceState.v];
        }
        if (parentTraceState) {
            // Reuse the existing trace state as a parent to avoid copying objects
            traceState = createW3cTraceState(STR_EMPTY, parentTraceState);
        }
        else {
            // fallback to creating a new trace state
            traceState = createW3cTraceState(value.serialize());
        }
    }
    else if (isW3cTraceState(value)) {
        traceState = value;
    }
    else {
        traceState = createW3cTraceState(isString(value) ? value : STR_EMPTY);
    }
    return _createOTelTraceState(traceState);
}
//# sourceMappingURL=traceState.js.map